import { Card, CardContent } from '../../../components/ui/card';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { APP_LANGUAGES } from '../../../lib/languages';

interface LanguageSelectionProps {
  onLanguageSelect: (languageId: string) => void;
  selectedLanguage: string;
  languageSelected: boolean;
}

/**
 * The choice is persisted onto the user by `POST /businesses` and drives both
 * the UI locale and the language of outbound email. It used to be required on
 * the first screen and then silently dropped at submit.
 *
 * The list comes from `lib/languages`, which is keyed to the translation
 * bundles that actually ship, rather than being retyped per screen.
 */
export function LanguageSelection({
  onLanguageSelect,
  selectedLanguage,
  languageSelected,
}: Readonly<LanguageSelectionProps>) {
  const { t } = useTranslation();

  return (
    <div className="p-6 space-y-4 bg-white rounded-lg shadow-sm">
      <div className="space-y-2 text-center">
        <h3 className="text-[1.25rem] leading-[120%] text-primary-black font-semibold tracking-[0%]">
          {t('accountSetup.chooseLanguage')}
        </h3>
        <p className="text-[1rem] leading-[120%] text-secondary">
          {t('accountSetup.chooseLanguageDescription')}
        </p>
      </div>

      <div className="grid w-full grid-cols-1 gap-4 mx-auto sm:grid-cols-2">
        {APP_LANGUAGES.map((language) => (
          <button
            key={language.id}
            className={cn(
              'p-0.5 rounded-[8px] transition-all duration-500 ease-linear',
              language.id === selectedLanguage && languageSelected
                ? 'bg-gradient-main'
                : 'bg-background gradient-hover'
            )}
            onClick={() => onLanguageSelect(language.id)}
          >
            <Card className="transition-all cursor-pointer hover:shadow-none bg-background rounded-[inherit]">
              <CardContent className="p-6 text-center">
                <div className="space-y-2">
                  <div className="text-[1.5rem] leading-none emoji">
                    {language.flag}
                  </div>
                  <p className="font-medium">{language.name}</p>
                </div>
              </CardContent>
            </Card>
          </button>
        ))}
      </div>
    </div>
  );
}
